import type { EbDashboard, EbSession, EbWorkspaceBundle, RecordItem } from './types';
import { fetchWithTimeout, storageGet, storageSet } from './browserCompat';

const messages: Record<string, string> = {
  PORTFOLIO_UNAVAILABLE:'This portfolio was taken. Refresh and review the delegate request.',
  COMMITTEE_CLOSED:'This committee is closed to new allocations.',
  DELEGATE_NOT_PENDING:'The delegate already has an allocation or is no longer waiting.',
  INVALID_CODE: 'The display name or EB access code is incorrect.',
  INVALID_ACCOUNT_LINK: 'The EB assigned code is incorrect or has been replaced. Ask the Super Admin for the current code.',
  RATE_LIMITED: 'Too many access attempts. Wait before trying again.',
  SESSION_EXPIRED: 'Your secure EB session expired. Enter your access code again.',
  EB_SUSPENDED: 'Your Executive Board access is suspended or revoked.',
  EB_NOT_ASSIGNED: 'Your committee assignment is still pending.',
  FORBIDDEN: 'Your EB role does not permit this action.',
  CONFLICT: 'This record changed on another device. Refresh and review before retrying.',
  VALIDATION: 'Check the required fields and try again.',
  CONFIG_CONFLICT: 'Scoring settings changed. Close and reopen the editor to review the latest version.',
  SCORECARD_LOCKED: 'This evaluation is locked by the Secretariat. Only a Super Admin can unlock it.',
  SCORECARD_NOT_CONFIGURED: 'Create the committee scorecard before entering scores.',
  WEIGHTS_INVALID: 'Enabled criterion weights must total 100%.',
  SCORED_CONFIG: 'This change would alter existing scores. Keep scored criteria, their numeric ranges/steps and scored periods.',
  USE_LIVE_SCORECARD: 'Open Live Scorecard for current scoring; legacy evaluation records are read-only.',
  MAINTENANCE_ACTIVE: 'Conference maintenance is active. Changes cannot be saved yet.',
  BACKEND_UNAVAILABLE: 'The EB operations service is temporarily unavailable. No change was confirmed.',
};

export class EbError extends Error {
  constructor(public code: string) { super(messages[code] ?? messages.BACKEND_UNAVAILABLE); }
}

type CacheEntry = { data: unknown; expiresAt: number };
const readCache = new Map<string, CacheEntry>();
const inFlightReads = new Map<string, Promise<unknown>>();
const actionVersions = new Map<string, number>();
let cacheEpoch = 0;
const cacheKey = (action: string, args: object) => `${action}:${JSON.stringify(args)}`;

export async function ebCall<T>(action: string, args: object = {}): Promise<T> {
  // Private cached reads must never survive an identity boundary.
  if (action === 'login' || action === 'logout') invalidateEbCache();
  let result: Response;
  try {
    result = await fetchWithTimeout('/api/eb', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin', cache: 'no-store',
      body: JSON.stringify({ action, args }),
    }, 25_000);
  } catch { throw new EbError('BACKEND_UNAVAILABLE'); }
  const payload = await result.json().catch(() => ({ error: 'BACKEND_UNAVAILABLE' })) as { data?: T; error?: string };
  if (!result.ok || payload.error || payload.data === undefined) {
    if (payload.error === 'SESSION_EXPIRED' || payload.error === 'EB_SUSPENDED') invalidateEbCache();
    throw new EbError(payload.error ?? 'BACKEND_UNAVAILABLE');
  }
  return payload.data;
}

export function peekEbCache<T>(action: string, args: object = {}): T | undefined {
  const cached = readCache.get(cacheKey(action, args));
  return cached && cached.expiresAt > Date.now() ? cached.data as T : undefined;
}

export function invalidateEbCache(action?: string) {
  if (!action) { cacheEpoch++; readCache.clear(); inFlightReads.clear(); actionVersions.clear(); return; }
  actionVersions.set(action, (actionVersions.get(action) ?? 0) + 1);
  for (const key of readCache.keys()) if (key.startsWith(`${action}:`)) readCache.delete(key);
  for (const key of inFlightReads.keys()) if (key.startsWith(`${action}:`)) inFlightReads.delete(key);
}

export function seedEbCache(action: string, args: object, data: unknown, maxAge = 30_000) {
  readCache.set(cacheKey(action, args), { data, expiresAt: Date.now() + maxAge });
}

export async function ebCached<T>(action: string, args: object = {}, maxAge = 30_000, force = false): Promise<T> {
  const key = cacheKey(action, args);
  const pending = inFlightReads.get(key);
  if (pending) return pending as Promise<T>;
  if (!force) {
    const cached = peekEbCache<T>(action, args);
    if (cached !== undefined) return cached;
  }
  const epoch = cacheEpoch; const version = actionVersions.get(action) ?? 0;
  const request = ebCall<T>(action, args)
    .then((data) => {
      if (epoch !== cacheEpoch || version !== (actionVersions.get(action) ?? 0)) {
        // A completed pre-mutation/session read is not a fresh confirmation.
        throw new EbError('CONFLICT');
      }
      readCache.set(key, { data, expiresAt: Date.now() + maxAge }); return data;
    })
    .finally(() => { if (inFlightReads.get(key) === request) inFlightReads.delete(key); });
  inFlightReads.set(key, request);
  return request;
}

const IDENTITY_KEY = 'dismun-eb-desktop-identity-v1';
export async function login(name: string, code: string, accountLinkCode: string) {
  let identity = storageGet(IDENTITY_KEY);
  if (!identity) {
    identity = Array.from(crypto.getRandomValues(new Uint8Array(32)), (v) => v.toString(16).padStart(2, '0')).join('');
    storageSet(IDENTITY_KEY, identity);
  }
  const result = await ebCall<{ session: EbSession }>('login', {
    name,
    code,
    account_link_code: accountLinkCode,
    identity,
    device: 'Desktop EB / Browser',
  });
  return result.session;
}
export const restoreSession = () => ebCall<EbSession>('session');
export const restoreWorkspace = () => ebCached<EbWorkspaceBundle>('workspace_bootstrap', {}, 20_000);
export const loadDashboard = () => ebCall<EbDashboard>('dashboard');
export const value = (input: unknown, fallback = '') =>
  typeof input === 'string' || typeof input === 'number' ? String(input) : fallback;
export const asRows = (input: unknown) => Array.isArray(input) ? input as RecordItem[] : [];

export const SUPABASE_URL = 'https://ucbkqmpbuhfwutfbbhmy.supabase.co';
export const SUPABASE_KEY = 'sb_publishable_Qj3aIC8y6lcl9t8RR4EH8w_1HgN9rcP';
export const avatarUrl = (path?: string | null) => path
  ? `${SUPABASE_URL}/storage/v1/object/public/eb-profile-pictures/${path.split('/').map(encodeURIComponent).join('/')}`
  : undefined;
